import { IOffer, ISanityVendorConfigs } from '@rbi-ctg/menu';
import { InRestaurantIncentiveType as IInRestaurantRedemptionEntryType } from 'generated/graphql-gateway';
import { LoyaltyEngineReward, LoyaltyOffer, LoyaltyReward } from 'state/loyalty/types';

export { IInRestaurantRedemptionEntryType };

export type InRestaurantIncentives = LoyaltyReward | LoyaltyEngineReward | IOffer | LoyaltyOffer;
// abstraction only for type accessibility
export type IRewardRedemption = {
  reward: LoyaltyReward;
  engineReward: NonNullable<LoyaltyEngineReward>;
};
export type IOfferRedemption = {
  offer: IOffer | LoyaltyOffer;
};

export type InRestaurantRedemptionObject = IRewardRedemption | IOfferRedemption;

export interface IInRestaurantRedemptionCartEntry {
  // represents an incentive id indicator
  incentiveId: string;
  // a unique id to reference the cart entry
  referenceId: string;
  // entry quantity
  quantity: number;
  // vendor configs associated to the entry item
  vendorConfigs: NonNullable<ISanityVendorConfigs>;
  // cart entry type, it represents the redemption object that was used to create the cart entry
  type: IInRestaurantRedemptionEntryType;
  // object to store all the needed data to help the item processing step
  details: InRestaurantRedemptionObject;
}

export interface IInRestaurantRedemptionRewardEntry extends IInRestaurantRedemptionCartEntry {
  type: IInRestaurantRedemptionEntryType.REWARD;
  details: IRewardRedemption;
}
export interface IInRestaurantRedemptionOfferEntry extends IInRestaurantRedemptionCartEntry {
  type: IInRestaurantRedemptionEntryType.OFFER | IInRestaurantRedemptionEntryType.LEGACY_OFFER;
  details: IOfferRedemption;
}

export type IInRestaurantRedemptionCart = Array<IInRestaurantRedemptionCartEntry>;

export interface IFindEntryOptions {
  type?: IInRestaurantRedemptionEntryType;
  keyPath?: string[];
  value?: unknown;
}
export interface IUseInRestaurantRedemptionCart {
  inRestaurantRedemptionCart: IInRestaurantRedemptionCart;
  isInRestaurantRedemptionCartEmpty: boolean;
  addInRestaurantRedemptionEntry: (redemptionObject: InRestaurantRedemptionObject) => boolean;
  removeInRestaurantRedemptionEntry: (cartEntry: IInRestaurantRedemptionCartEntry) => void;
  updateInRestaurantRedemptionEntryQuantity(
    cartEntry: IInRestaurantRedemptionCartEntry,
    quantity: number
  ): void;
  resetInRestaurantRedemptionCart: () => void;
  clearInRestaurantRedemptionAllRewards: () => void;
  existEntryTypeInRestaurantRedemptionCart: (
    entryType: IInRestaurantRedemptionEntryType
  ) => boolean;
  findInRestaurantCartEntryByIncentive: (
    incentive: InRestaurantIncentives
  ) => IInRestaurantRedemptionCartEntry | undefined;
}

// type guards
export const isLoyaltyOffer = (entity: any): entity is LoyaltyOffer =>
  entity?._type === 'systemwideOffer';
export const isLegacyOffer = (entity: any): entity is IOffer => entity?._type === 'offer';
export const isLoyaltyReward = (entity: any): entity is LoyaltyReward =>
  entity?.__typename?.toLowerCase() === 'reward' && !!entity?._id;
export const isEngineReward = (entity: any): entity is LoyaltyEngineReward =>
  entity?.__typename?.toLowerCase() === 'reward' && !!entity?.id;

export const isRedemptionObjectOffer = (entry: any): entry is IOfferRedemption =>
  isLoyaltyOffer(entry?.offer);
export const isRedemptionObjectLegacyOffer = (entry: any): entry is IOfferRedemption =>
  isLegacyOffer(entry?.offer);
export const isRedemptionObjectReward = (entry: any): entry is IRewardRedemption =>
  isLoyaltyReward(entry?.reward) || isEngineReward(entry?.engineReward);

export const isRewardCartEntry = (
  cartEntry: IInRestaurantRedemptionCartEntry
): cartEntry is IInRestaurantRedemptionRewardEntry =>
  cartEntry.type === IInRestaurantRedemptionEntryType.REWARD;

export const isRedemptionObjectStackableOffer = (entry: any): boolean =>
  isLoyaltyOffer(entry?.offer) && entry.offer.isStackable;
